import { useState, useEffect, useRef } from 'react';
import ConditionalRender from "../../helpers/ConditionalRender";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { encodingForModel } from 'js-tiktoken';
import { getModelPricing, FALLBACK_PRICING, resolveModelPricing, ModelPricing } from './pricing';
import { openAiModels, openRouterModels, googleModels } from '../Constants';
import { findIntegrationValue } from "../../helpers/utils";
import { showError } from "../../utils/showAlert";
import api from "../../api/Base";
import './style.scss';

type ChatMessage = {
    sender: string;
    message: string;
    cost?: number;
    prompt_tokens?: number;
    completion_tokens?: number;
};

const FALLBACK_SUPPORTED_DATABASES = [
    'CockroachDB',
    'DB2',
    'Informix',
    'MariaDB',
    'MongoDB',
    'MySQL',
    'Oracle',
    'PostgreSQL',
    'SAP HANA',
    'Snowflake',
    'SQL Server',
    'SAP (Sybase) ASE'
];

const DISPLAY_NAME_BY_TYPE: Record<string, string> = {
    cockroachdb: 'CockroachDB',
    db2: 'DB2',
    informix: 'Informix',
    mongodb: 'MongoDB',
    mysql: 'MySQL',
    oracle: 'Oracle',
    postgresql: 'PostgreSQL',
    saphana: 'SAP HANA',
    snowflake: 'Snowflake',
    sqlserver: 'SQL Server',
    sybase: 'SAP (Sybase) ASE'
};

const resolveSupportedDatabases = (types: any[]): string[] => {
    const byLowerName = new Map<string, string>();

    types.forEach((item) => {
        const rawType = (item?.type ?? item?.datasource_type ?? '').toString().trim().toLowerCase();
        const rawDisplay = (item?.display ?? item?.display_name ?? '').toString().trim();
        const displayName = rawDisplay || DISPLAY_NAME_BY_TYPE[rawType] || rawType;

        if (!displayName) return;
        byLowerName.set(displayName.toLowerCase(), displayName);
    });

    // MariaDB is supported but not exposed as a separate datasource type.
    byLowerName.set('mariadb', 'MariaDB');

    return Array.from(byLowerName.values()).sort((a, b) => a.localeCompare(b));
};

const buildSystemPrompt = (supportedDatabases: string[]): string => {
    const authoritativePlatforms = (supportedDatabases.length ? supportedDatabases : FALLBACK_SUPPORTED_DATABASES).join(', ');

    return `You are DBmarlin Co-pilot, a database performance assistant for DBmarlin users.
Authoritative product context for this deployment:
- Supported database platforms: ${authoritativePlatforms}
- Treat this platform list as source-of-truth over model pretraining.
Rules:
1. Never invent DBmarlin features or platform support.
2. If asked whether DBmarlin supports a platform:
   - If it is in the supported platform list, answer yes and continue with relevant DBmarlin guidance.
   - If it is not in the supported platform list, answer that it is not currently listed as supported in this deployment.
3. If required context is missing, explicitly say what is missing in one sentence.
4. Keep responses concise and actionable.
5. Suggest DBmarlin workflows only when relevant to the user's request.`;
};

const DBMARLIN_AI = 'DBmarlin Co-pilot'
const HIGH_LATENCY_MODELS = new Set<string>([
    'gpt-5.2',
    'gpt-5.2-codex',
    'gpt-5.2-pro',
    'openai/gpt-5.2',
    'openai/gpt-5.2-codex',
    'openai/gpt-5.2-pro'
]);

// Helpers to safely handle Base64 decoding for keys stored in localStorage
const looksBase64 = (s: string) => /^[A-Za-z0-9+/=]+$/.test(s) && s.length % 4 === 0;

const decodeIfBase64 = (raw: string) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    if (!looksBase64(trimmed)) return raw;
    try {
        const decoded = atob(trimmed);
        // If decoded contains non-printable characters, treat as raw (not actually base64 for our intent)
        if (/[^\x20-\x7E]/.test(decoded)) return raw;
        return decoded;
    } catch {
        return raw;
    }
};

const ChatComponent = (props: { data: string, coPilotIntegration?: any[] }) => {
    // Helper function to get integration value
    const getIntegrationValue = (fieldName: string) => {
        if (!props.coPilotIntegration) return null;
        const param = props.coPilotIntegration.find(p => p.name === `ai.provider.${fieldName}`);
        return param ? param.value : null;
    };

    const [loading, setLoading] = useState(false);
    const [showDBmarlin, setShowDBmarlin] = useState(true);
    const [question, setQuestion] = useState(props.data);
    const [userEditedPrompt, setUserEditedPrompt] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [tooltipText, setTooltipText] = useState('');
    const [copiedCode, setCopiedCode] = useState<string | null>(null);
    const [copiedMessage, setCopiedMessage] = useState<number | null>(null);
    const [supportedDatabases, setSupportedDatabases] = useState<string[]>([]);

    // Function to copy text to clipboard with feedback
    const copyToClipboardWithFeedback = (text: string, setCopied: (value: any) => void, value: any) => {
        if (navigator.clipboard && window.isSecureContext) {
            // Modern API for HTTPS
            navigator.clipboard.writeText(text)
                .then(() => {
                    setCopied(value);
                    setTimeout(() => setCopied(null), 2000);
                })
                .catch((err) => console.error('Clipboard write failed:', err));
        } else {
            // Fallback for HTTP
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopied(value);
                setTimeout(() => setCopied(null), 2000);
            } catch (err) {
                console.error('Fallback copy failed:', err);
            }
            document.body.removeChild(textArea);
        }
    };
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const [pricing, setPricing] = useState<Record<string, any> | null>(null);
    const [savedKey, setSavedKey] = useState('');
    const [totalCost, setTotalCost] = useState<number>(0);
    // Ref for aborting fetch
    const abortControllerRef = useRef<AbortController | null>(null);
    // Use integration data if available, otherwise fall back to localStorage
    const integrationProvider = getIntegrationValue('name');
    const integrationModel = getIntegrationValue('model');
    const integrationEndpoint = getIntegrationValue('url');

    let userSavedProvider = integrationProvider || window.localStorage.getItem('openAIProvider') || 'OpenAI';
    let userSavedModel = integrationModel || window.localStorage.getItem('openAIModel');
    const [aiProvider, setAiProvider] = useState<string>(userSavedProvider);
    const [openAiModel, setOpenAiModel] = useState<string>(userSavedModel || 'gpt-4-turbo');
    const [openRouterKey, setOpenRouterKey] = useState<string>('');
    const [googleKey, setGoogleKey] = useState<string>('');
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [azureEndpoint, setAzureEndpoint] = useState<string>('');
    const [azureKey, setAzureKey] = useState<string>('');
    const [openAiBaseUrl, setOpenAiBaseUrl] = useState<string>('');

    // New state for AI Settings modal
    const [showSettings, setShowSettings] = useState(false);
    const [sortColumn, setSortColumn] = useState<'model' | 'input' | 'output'>('model');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Local state for settings modal (not saved until save button is pressed)
    const [localKeyInput, setLocalKeyInput] = useState('');
    const [localOpenRouterKeyInput, setLocalOpenRouterKeyInput] = useState('');
    const [localGoogleKeyInput, setLocalGoogleKeyInput] = useState('');
    const [localProvider, setLocalProvider] = useState('OpenAI');
    const [localModel, setLocalModel] = useState('gpt-4-turbo');
    const [useSystemSettings, setUseSystemSettings] = useState(false);
    const [systemSettingsAvailable, setSystemSettingsAvailable] = useState(false);
    const [localAzureEndpoint, setLocalAzureEndpoint] = useState('');
    const [localOpenAiBaseUrl, setLocalOpenAiBaseUrl] = useState('');
    const systemPrompt = buildSystemPrompt(supportedDatabases);

    // Validate API key before saving
    const validateApiKey = async (provider: string, key: string): Promise<string | null> => {
        try {
            let response;
            if (provider === 'OpenAI') {
                // Get the base URL from system settings or local input, default to standard OpenAI endpoint
                const baseUrl = (useSystemSettings ? openAiBaseUrl : localOpenAiBaseUrl || 'https://api.openai.com').trim();
                const apiUrl = baseUrl.endsWith('/') ? `${baseUrl}v1/models` : `${baseUrl}/v1/models`;

                response = await fetch(apiUrl, {
                    headers: { 'Authorization': `Bearer ${key}` }
                });
                if (!response.ok) {
                    let errorMsg = '';
                    try {
                        const data = await response.json();
                        errorMsg = data?.error?.message || response.statusText;
                    } catch {
                        errorMsg = response.statusText || 'Invalid API key or insufficient permissions.';
                    }
                    return errorMsg;
                }
                return null;
            } else if (provider === 'OpenRouter') {
                // Try /api/v1/auth/key for validation
                response = await fetch('https://openrouter.ai/api/v1/auth/key', {
                    headers: { 'Authorization': `Bearer ${key}` }
                });
                if (!response.ok) {
                    let errorMsg = '';
                    try {
                        const data = await response.json();
                        errorMsg = data?.error?.message || response.statusText;
                    } catch {
                        errorMsg = response.statusText || 'Invalid OpenRouter API key.';
                    }
                    return errorMsg;
                }
                // Optionally, check for error in body
                let data;
                try {
                    data = await response.json();
                } catch {
                    return 'Invalid response from OpenRouter API.';
                }
                if (data?.error) {
                    return data.error.message || 'Invalid OpenRouter API key.';
                }
                return null;
            } else if (provider === 'AzureOpenAI') {
                // For Azure OpenAI, validate against the configured Target URI using a minimal non-streaming request.
                const targetUriRaw = (useSystemSettings ? azureEndpoint : localAzureEndpoint || '').trim();
                if (!targetUriRaw) {
                    return 'Please enter an Azure Target URI before validating the API key.';
                }

                let url: URL;
                try {
                    url = new URL(targetUriRaw);
                } catch {
                    return 'Invalid Azure OpenAI Target URI.';
                }

                response = await fetch(url.toString(), {
                    method: 'POST',
                    headers: {
                        'api-key': key,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        messages: [{ role: 'user', content: 'test' }],
                        max_tokens: 1
                    })
                });

                if (!response.ok) {
                    let errorMsg = '';
                    try {
                        const data = await response.json();
                        // Azure typically returns { error: { message, code, ... } }
                        errorMsg = data?.error?.message || response.statusText || 'Invalid Azure OpenAI API key or Target URI.';
                    } catch {
                        errorMsg = response.statusText || 'Invalid Azure OpenAI API key or Target URI.';
                    }
                    return errorMsg;
                }
                return null;
            } else if (provider === 'Google') {
                // Validate Google API Key
                response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${key}`);
                if (!response.ok) {
                    let errorMsg = '';
                    try {
                        const data = await response.json();
                        errorMsg = data?.error?.message || response.statusText;
                    } catch {
                        errorMsg = response.statusText || 'Invalid Google API key.';
                    }
                    return errorMsg;
                }
                return null;
            } else {
                return null;
            }
        } catch (e) {
            return 'Network error while validating API key.';
        }
    };


    // Function to fetch system integration data
    const fetchSystemIntegrationData = async () => {
        try {
            const response = await api.get('/parameter?name=ai.provider.%25');

            if (response.status === 200 && response.data) {
                return response.data;
            }
        } catch (error) {
            showError('Co-pilot Error', 'Failed to load system integration settings.');
        }
        return [];
    };

    // Function to check and load system settings
    const checkSystemSettings = async () => {
        const data = await fetchSystemIntegrationData();
        setSystemSettingsAvailable(data.length > 0);
        return data;
    };

    // Function to apply system settings to form
    const applySystemSettings = (data: any[]) => {
        if (data.length > 0) {
            const systemProvider = findIntegrationValue('name', data, 'ai.provider') || 'OpenAI';
            const systemModel = findIntegrationValue('model', data, 'ai.provider') || 'gpt-4-turbo';
            const systemApiKey = findIntegrationValue('plainkey', data, 'ai.provider') || '';
            const systemEndpoint = findIntegrationValue('url', data, 'ai.provider') || '';

            setLocalProvider(systemProvider);
            setLocalModel(systemModel);
            if (systemProvider === 'OpenAI') {
                setLocalKeyInput(systemApiKey);
                setLocalOpenAiBaseUrl(systemEndpoint);
            } else if (systemProvider === 'OpenRouter') {
                setLocalOpenRouterKeyInput(systemApiKey);
            } else if (systemProvider === 'AzureOpenAI') {
                setLocalKeyInput(systemApiKey);
                setLocalAzureEndpoint(systemEndpoint);
            }
            // Keep main Azure state in sync with system values
            if (systemProvider === 'AzureOpenAI') {
                setAzureEndpoint(systemEndpoint);
            } else if (systemProvider === 'OpenAI') {
                setOpenAiBaseUrl(systemEndpoint);
            } else if (systemProvider === 'Google') {
                setLocalGoogleKeyInput(systemApiKey);
            }
        }
    };

    // Handle system settings checkbox change
    const handleSystemSettingsChange = async (checked: boolean) => {
        setUseSystemSettings(checked);
        if (checked) {
            // Ensure we have fresh system settings data when checking
            const freshSystemData = await fetchSystemIntegrationData();
            applySystemSettings(freshSystemData);
        } else {
            // Clear all fields when unchecking system settings
            setLocalProvider('OpenAI');
            setLocalModel('gpt-4-turbo');
            setLocalKeyInput('');
            setLocalOpenRouterKeyInput('');
            setLocalAzureEndpoint('');
            setLocalGoogleKeyInput('');

            // Also clear main state variables to prevent re-population
            setSavedKey('');
            setOpenRouterKey('');
            setAzureEndpoint('');
            setAzureKey('');

            // Clear from localStorage to ensure system keys don't persist
            window.localStorage.removeItem('openAIKEY');
            window.localStorage.removeItem('openRouterKEY');
            window.localStorage.removeItem('azureOpenAIEndpoint');
            window.localStorage.removeItem('azureOpenAIKEY');
            window.localStorage.removeItem('googleKEY');
        }
    };

    // Save provider and model
    const saveProvider = (value: string) => {
        if (useSystemSettings) {
            // Don't change provider when system settings are applied
            return;
        }

        setLocalProvider(value);
        // Reset model to default for provider
        if (value === 'OpenAI') {
            setLocalModel('gpt-4-turbo');
        } else if (value === 'OpenRouter') {
            setLocalModel('openai/gpt-3.5-turbo');
        } else if (value === 'AzureOpenAI') {
            setLocalModel('gpt-4o');
        } else if (value === 'Google') {
            setLocalModel('gemini-1.5-flash');
        }

        // Populate the appropriate local key input from localStorage so users don't need to re-enter keys
        if (value === 'OpenAI') {
            const raw = window.localStorage.getItem('openAIKEY') || '';
            setLocalKeyInput(decodeIfBase64(raw));
        } else if (value === 'AzureOpenAI') {
            const raw = window.localStorage.getItem('azureOpenAIKEY') || '';
            setLocalKeyInput(decodeIfBase64(raw));
        } else if (value === 'OpenRouter') {
            const raw = window.localStorage.getItem('openRouterKEY') || '';
            setLocalOpenRouterKeyInput(decodeIfBase64(raw));
        } else if (value === 'Google') {
            const raw = window.localStorage.getItem('googleKEY') || '';
            setLocalGoogleKeyInput(decodeIfBase64(raw));
        }
    };

    useEffect(() => {
        const fetchSupportedDatabases = async () => {
            try {
                const response = await api.get('datasource/type?collect=true');
                if (Array.isArray(response?.data)) {
                    const resolved = resolveSupportedDatabases(response.data);
                    if (resolved.length > 0) {
                        setSupportedDatabases(resolved);
                    }
                }
            } catch (error: any) {
                console.error('Failed to retrieve datasource types for Co-pilot prompt.', error);
            }
        };

        void fetchSupportedDatabases();
    }, []);

    useEffect(() => {
        const termsAgreed = window.localStorage.getItem('openAITerms');

        // Use integration API key if available, otherwise use localStorage
        const integrationApiKey = getIntegrationValue('plainkey');
        const integrationProvider = getIntegrationValue('name');

        // If integration provider is System, use the integration API key for OpenAI
        const rawOpenAI = (integrationProvider === 'System' && integrationApiKey) || integrationApiKey || window.localStorage.getItem('openAIKEY') || '';
        const rawOpenRouter = window.localStorage.getItem('openRouterKEY') || '';

        // Azure per-user overrides for target URI (fall back to integration values)
        const storedAzureEndpoint = window.localStorage.getItem('azureOpenAIEndpoint');
        const effectiveAzureEndpoint = (storedAzureEndpoint || integrationEndpoint || '') as string;

        // OpenAI per-user overrides for base URL (fall back to integration values)
        const storedOpenAiBaseUrl = window.localStorage.getItem('openAIBaseUrl');
        const effectiveOpenAiBaseUrl = (storedOpenAiBaseUrl || integrationEndpoint || '') as string;

        const rawGoogle = window.localStorage.getItem('googleKEY') || '';
        const rawAzureKey = window.localStorage.getItem('azureOpenAIKEY') || '';

        // Safely decode if the values look like Base64; otherwise use as-is
        const decodedOpenAI = decodeIfBase64(rawOpenAI);
        const decodedOpenRouter = decodeIfBase64(rawOpenRouter);
        const decodedGoogle = decodeIfBase64(rawGoogle);
        const decodedAzure = decodeIfBase64(rawAzureKey);

        let initialSavedKey = decodedOpenAI;

        // If there is no integration API key configured, prefer the per-user Azure key for Azure provider
        if (!integrationApiKey && aiProvider === 'AzureOpenAI') {
            initialSavedKey = decodedAzure || decodedOpenAI;
        }

        setSavedKey(initialSavedKey);
        setOpenRouterKey(decodedOpenRouter);
        setAzureEndpoint(effectiveAzureEndpoint);
        setOpenAiBaseUrl(effectiveOpenAiBaseUrl);
        setGoogleKey(decodedGoogle);
        setAzureKey(decodedAzure);
        setAgreeTerms(termsAgreed ? JSON.parse(termsAgreed) : false);

        // Fetch pricing on mount
        getModelPricing().then(setPricing);
    }, [props.coPilotIntegration, aiProvider]);

    useEffect(() => {
        if (props.data) {
            setShowDBmarlin(false);
            void askGPT(props.data);
            // --- FIX: check for both OpenAI and OpenRouter keys ---
            if (!(savedKey || openRouterKey || googleKey)) {
                setQuestion(props.data);
            }
        }
    }, [props.data, savedKey, openRouterKey, googleKey]);

    // When Provider is None, always use latest props.data as prompt unless user has edited
    useEffect(() => {
        if (aiProvider === "None") {
            if (!userEditedPrompt) {
                if (props.data && props.data.trim() !== "") {
                    setQuestion(props.data);
                } else if (!question || question.trim() === "") {
                    setQuestion("No prompt available. Please use a feature button to generate a prompt.");
                }
            }
        }
        // Reset userEditedPrompt if provider changes away from None
        if (aiProvider !== "None") {
            setUserEditedPrompt(false);
        }
    }, [aiProvider, props.data]);

    // Handle provider change logic when local provider changes
    useEffect(() => {
        if (localProvider === "None") {
            setQuestion(props.data || "Enter your prompt here...");
        }
    }, [localProvider, props.data]);

    // Initialize local state when settings modal opens
    useEffect(() => {
        if (showSettings) {
            const initializeModal = async () => {
                setLocalProvider(aiProvider);
                setLocalModel(openAiModel);

                // Check for saved useSystemSettings preference in localStorage
                const savedUseSystemSettings = window.localStorage.getItem('useSystemSettings');
                const shouldUseSystemSettings = savedUseSystemSettings ? JSON.parse(savedUseSystemSettings) : false;

                // Check if current settings came from system integration
                const isUsingSystemSettings = !!(integrationProvider || integrationModel || getIntegrationValue('plainkey'));

                // Use saved preference if exists, otherwise fall back to integration detection
                const finalUseSystemSettings = savedUseSystemSettings !== null ? shouldUseSystemSettings : isUsingSystemSettings;

                // Check for system settings first
                const systemData = await checkSystemSettings();

                // Set the checkbox state
                setUseSystemSettings(finalUseSystemSettings);

                if (finalUseSystemSettings) {
                    // If using system settings, clear local inputs and apply system data
                    setLocalKeyInput('');
                    setLocalOpenRouterKeyInput('');
                    if (systemData.length > 0) {
                        applySystemSettings(systemData);
                    }
                } else {
                    // If not using system settings, populate from saved local values
                    if (aiProvider === 'AzureOpenAI') {
                        setLocalKeyInput(azureKey || savedKey);
                    } else {
                        setLocalKeyInput(savedKey);
                    }
                    setLocalOpenRouterKeyInput(openRouterKey);
                    setLocalAzureEndpoint(azureEndpoint);
                    setLocalOpenAiBaseUrl(openAiBaseUrl);
                    setLocalGoogleKeyInput(googleKey);
                }
            };

            initializeModal();
        }
    }, [showSettings]);

    // Helper to determine if a valid key is present for the selected provider
    const hasValidProviderKey = () => {
        if (aiProvider === 'OpenAI') {
            return !!savedKey;
        } else if (aiProvider === 'OpenRouter') {
            return !!openRouterKey;
        } else if (aiProvider === 'AzureOpenAI') {
            // For Azure, require an API key and a Target URI
            return !!savedKey && !!azureEndpoint;
        } else if (aiProvider === 'Google') {
            return !!googleKey;
        }
        return false;
    };

    const scrollToBottom = () => {
        // Use a more robust approach with multiple attempts to ensure scrolling happens
        const scrollAttempt = (attempt = 0) => {
            setTimeout(() => {
                if (chatContainerRef.current) {
                    const element = chatContainerRef.current;
                    const parent = element.parentElement;

                    // Scroll both the message list and its parent (chat-content)
                    if (parent) {
                        parent.scrollTop = parent.scrollHeight;
                    }
                    element.scrollTop = element.scrollHeight;

                    // If we're still not at the bottom and haven't exceeded max attempts, try again
                    const isScrolledToBottom = element.scrollHeight - element.clientHeight <= element.scrollTop + 10;
                    if (!isScrolledToBottom && attempt < 3) {
                        scrollAttempt(attempt + 1);
                    }
                }
            }, 150 * (attempt + 1)); // Increasing delay with each attempt
        };

        scrollAttempt();
    };
    const askGPT = async (defaultQuestion?: string, apiKey?: string) => {
        // Append the user's message to the chat history
        appendMessage('You', defaultQuestion || question);
        appendMessage(DBMARLIN_AI, '');
        scrollToBottom();

        // Build the full conversation history for the API
        const conversationMessages = chatMessages
            .filter(msg => msg.sender === 'You' || msg.sender === DBMARLIN_AI)
            .map(msg => ({
                role: msg.sender === 'You' ? 'user' : 'assistant',
                content: msg.message
            }));
        // Add the latest user question (not yet in chatMessages)
        conversationMessages.push({ role: 'user', content: defaultQuestion || question });

        // Prepare messages array for OpenAI/OpenRouter API
        const messages = [
            { role: "system", content: systemPrompt },
            ...conversationMessages
        ];

        // Estimate input tokens using js-tiktoken
        let promptTokens = 0;
        let enc: any = null;
        try {
            // Only allow known models for encodingForModel
            const knownModels = [
                "gpt-3.5-turbo", "gpt-3.5-turbo-0301", "gpt-3.5-turbo-0613", "gpt-3.5-turbo-16k",
                "gpt-4", "gpt-4-0314", "gpt-4-0613", "gpt-4-32k", "gpt-4-32k-0314", "gpt-4-32k-0613"
            ];
            const modelForEncoding = knownModels.includes(openAiModel) ? openAiModel : "gpt-3.5-turbo";
            enc = encodingForModel(modelForEncoding as any);

            // Calculate tokens for all messages in the conversation
            promptTokens = messages.reduce((total, msg) => {
                return total + enc.encode(msg.content).length;
            }, 0);
        } catch (e) {
            promptTokens = prompt.length; // fallback: 1 char = 1 token (not accurate)
        }

        setLoading(true);
        let finished = false;
        let aiKey = savedKey || apiKey || '';
        if (aiProvider === 'OpenRouter') aiKey = openRouterKey || apiKey || '';
        if (aiProvider === 'Google') aiKey = googleKey || apiKey || '';

        let lastMessage = '';
        let outputTokenCount = 0;
        let lastUiUpdateAt = 0;
        const UI_UPDATE_THROTTLE_MS = 60;

        // Setup AbortController for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Streaming logic with output token estimation
        const handleChunk = (delta: string) => {
            if (abortController.signal.aborted) return;
            lastMessage += delta;
            if (enc) {
                outputTokenCount += enc.encode(delta).length;
            } else {
                outputTokenCount += delta.length; // fallback
            }
            const now = Date.now();
            if (now - lastUiUpdateAt >= UI_UPDATE_THROTTLE_MS) {
                updateLastMessage(lastMessage);
                scrollToBottom();
                lastUiUpdateAt = now;
            }
        };
        const handleDone = () => {
            // Ensure final text is rendered if the last chunks were throttled.
            if (lastMessage) {
                updateLastMessage(lastMessage);
                scrollToBottom();
            }
            setQuestion('');
            setLoading(false);
            finished = true;
            abortControllerRef.current = null;
            // After streaming, update the last message with estimated tokens and cost
            // Calculate cost before updating state (must be synchronous)
            let cost: number | undefined = undefined;
            if (pricing) {
                const modelPricing = resolveModelPricing(pricing, FALLBACK_PRICING, aiProvider, openAiModel);
                if (modelPricing) {
                    const inputPerToken = Number(modelPricing.input_cost_per_token);
                    const outputPerToken = Number(modelPricing.output_cost_per_token);
                    if (!isNaN(inputPerToken) && !isNaN(outputPerToken)) {
                        cost = (promptTokens * inputPerToken) + (outputTokenCount * outputPerToken);
                    } else {
                        const inputPer1k = Number(modelPricing.input_cost_per_1k_tokens) || 0;
                        const outputPer1k = Number(modelPricing.output_cost_per_1k_tokens) || 0;
                        cost = ((promptTokens * inputPer1k) + (outputTokenCount * outputPer1k)) / 1000;
                    }
                }
            }
            setChatMessages((prevMessages) => {
                if (prevMessages.length === 0) return prevMessages;
                const updated = [...prevMessages];
                updated[updated.length - 1] = {
                    ...updated[updated.length - 1],
                    prompt_tokens: promptTokens,
                    completion_tokens: outputTokenCount,
                    cost
                };
                return updated;
            });
            if (typeof cost === 'number') {
                const finalCost = cost; // Store in local variable to ensure it's defined in callback
                setTotalCost(prev => prev + finalCost);
            }
            if (enc) enc.free && enc.free();
        };
        const handleError = (e: any) => {
            setLoading(false);
            deleteEmptyMessage();
            if (!(e && (e.name === 'AbortError' || e.message === 'BodyStreamBuffer was aborted'))) {
                showError('Co-pilot Error', e?.message || 'Streaming error');
            }
            abortControllerRef.current = null;
        };

        // Helper to process streaming response (OpenAI & OpenRouter)
        const processStream = async (
            reader: ReadableStreamDefaultReader<Uint8Array>,
            onChunk: (text: string) => void,
            onDone: () => void,
            onError: (err: any) => void
        ) => {
            const decoder = new TextDecoder();
            let buffer = '';
            let doneCalled = false;
            try {
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) {
                        const trimmed = line.trim();
                        if (!trimmed || !trimmed.startsWith('data:')) continue;
                        const data = trimmed.replace(/^data:\s*/, '');
                        if (data === '[DONE]') {
                            if (!doneCalled) {
                                onDone();
                                doneCalled = true;
                            }
                            return;
                        }
                        try {
                            const parsed = JSON.parse(data);
                            const delta = parsed.choices?.[0]?.delta?.content || parsed.choices?.[0]?.message?.content || '';
                            if (delta) onChunk(delta);
                        } catch (e) { }
                    }
                }
                if (!doneCalled) onDone();
            } catch (err) {
                onError(err);
                if (!doneCalled) onDone();
            }
        };
        // Helper to process streaming response from OpenAI Responses API
        const processResponsesStream = async (
            reader: ReadableStreamDefaultReader<Uint8Array>,
            onChunk: (text: string) => void,
            onDone: () => void,
            onError: (err: any) => void
        ) => {
            const decoder = new TextDecoder();
            let buffer = '';
            let doneCalled = false;
            try {
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) {
                        const trimmed = line.trim();
                        if (!trimmed || !trimmed.startsWith('data:')) continue;
                        const data = trimmed.replace(/^data:\s*/, '');
                        if (data === '[DONE]') {
                            if (!doneCalled) {
                                onDone();
                                doneCalled = true;
                            }
                            return;
                        }
                        try {
                            const parsed = JSON.parse(data);
                            // Responses streaming typically emits:
                            // { type: "response.output_text.delta", delta: "..." }
                            const delta =
                                (parsed?.type === 'response.output_text.delta' && typeof parsed?.delta === 'string'
                                    ? parsed.delta
                                    : '') ||
                                (typeof parsed?.delta === 'string' ? parsed.delta : '');
                            if (delta) onChunk(delta);
                        } catch {
                            // Ignore malformed partial lines and continue parsing stream
                        }
                    }
                }
                if (!doneCalled) onDone();
            } catch (err) {
                onError(err);
                if (!doneCalled) onDone();
            }
        };

        try {
            if (aiProvider === "OpenAI" && aiKey) {
                // Get the base URL, default to standard OpenAI endpoint
                const baseUrl = (openAiBaseUrl || 'https://api.openai.com').trim();
                const isResponsesModel = /^(gpt-5\.2(?:-pro|-codex)?|gpt-5\.3-codex)$/i.test(openAiModel);
                const isHighLatencyModel = /^gpt-5\.2-pro$/i.test(openAiModel);

                if (isResponsesModel) {
                    const apiUrl = baseUrl.endsWith('/') ? `${baseUrl}v1/responses` : `${baseUrl}/v1/responses`;
                    const response = await fetch(apiUrl, {
                        method: "POST",
                        headers: {
                            "Authorization": `Bearer ${aiKey}`,
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify({
                            model: openAiModel,
                            input: messages.map(m => ({ role: m.role, content: m.content })),
                            stream: true,
                            ...(isHighLatencyModel
                                ? {
                                    reasoning: { effort: "medium" },
                                    max_output_tokens: 1200
                                }
                                : {})
                        }),
                        signal: abortController.signal
                    });

                    if (!response.ok) {
                        let errorMsg = `OpenAI error: ${response.statusText || `HTTP ${response.status}`}`;
                        try {
                            const data = await response.json();
                            const message = data?.error?.message;
                            if (message) errorMsg = `OpenAI error: ${message}`;
                        } catch {
                            // keep default error message
                        }
                        throw new Error(errorMsg);
                    }

                    lastMessage = '';
                    outputTokenCount = 0;
                    const reader = response.body?.getReader();
                    if (!reader) {
                        throw new Error("OpenAI error: No response body");
                    }
                    await processResponsesStream(reader, handleChunk, handleDone, handleError);
                } else {
                    const apiUrl = baseUrl.endsWith('/') ? `${baseUrl}v1/chat/completions` : `${baseUrl}/v1/chat/completions`;
                    const response = await fetch(apiUrl, {
                        method: "POST",
                        headers: {
                            "Authorization": `Bearer ${aiKey}`,
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify({
                            model: openAiModel,
                            messages: messages,
                            stream: true
                        }),
                        signal: abortController.signal
                    });
                    if (!response.ok || !response.body) {
                        let errorMsg = `OpenAI error: ${response.statusText || `HTTP ${response.status}`}`;
                        try {
                            const data = await response.json();
                            const message = data?.error?.message;
                            if (message) errorMsg = `OpenAI error: ${message}`;
                        } catch {
                            // keep default error message
                        }
                        throw new Error(errorMsg);
                    }
                    lastMessage = '';
                    outputTokenCount = 0;
                    const reader = response.body.getReader();
                    await processStream(reader, handleChunk, handleDone, handleError);
                }
            } else if (aiProvider === "OpenRouter" && openRouterKey) {
                const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${openRouterKey}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        model: openAiModel,
                        messages: messages,
                        stream: true
                    }),
                    signal: abortController.signal
                });
                if (!response.ok || !response.body) {
                    throw new Error(`OpenRouter error: ${response.statusText || `HTTP ${response.status}`}`);
                }
                lastMessage = '';
                outputTokenCount = 0;
                const reader = response.body.getReader();
                await processStream(reader, handleChunk, handleDone, handleError);
            } else if (aiProvider === "AzureOpenAI" && aiKey) {
                const raw = (azureEndpoint || '').trim();
                if (!raw) {
                    throw new Error("Azure OpenAI Target URI is not configured.");
                }

                let url: string;

                try {
                    // Expect the user to paste the full Target URI from Azure Portal
                    const azureUrl = new URL(raw);
                    url = azureUrl.toString();
                } catch {
                    throw new Error("Invalid Azure OpenAI Target URI. Please paste the full URI from the Azure portal.");
                }

                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "api-key": aiKey,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        // For Azure, model is typically the deployment name; reuse openAiModel
                        model: openAiModel,
                        messages: messages,
                        stream: true
                    }),
                    signal: abortController.signal
                });
                if (!response.ok || !response.body) {
                    throw new Error(`Azure OpenAI error: ${response.statusText || `HTTP ${response.status}`}`);
                }
                lastMessage = '';
                outputTokenCount = 0;
                const reader = response.body.getReader();
                await processStream(reader, handleChunk, handleDone, handleError);
            } else if (aiProvider === "Google" && googleKey) {
                // Google Gemini API

                // Convert messages to Google format
                const contents = messages.filter(m => m.role !== 'system').map(m => ({
                    role: m.role === 'user' ? 'user' : 'model',
                    parts: [{ text: m.content }]
                }));

                // Add system instruction if supported (Gemini 1.5+)
                const systemInstruction = {
                    parts: [{ text: systemPrompt }]
                };

                const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${openAiModel}:streamGenerateContent?key=${googleKey}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        contents,
                        systemInstruction,
                        generationConfig: {
                            temperature: 0.7
                        }
                    }),
                    signal: abortController.signal
                });

                if (!response.ok) {
                    let errorMsg = `Google API error: ${response.statusText || `HTTP ${response.status}`}`;
                    try {
                        const data = await response.json();
                        const message = data?.error?.message || data?.[0]?.error?.message;
                        if (message) {
                            errorMsg = `Google API error: ${message}`;
                        }
                    } catch {
                        // Keep the statusText error if JSON parsing fails
                    }
                    throw new Error(errorMsg);
                }

                lastMessage = '';
                outputTokenCount = 0;
                const reader = response.body?.getReader();
                if (!reader) throw new Error("No response body");

                const decoder = new TextDecoder();
                let buffer = '';

                try {
                    while (true) {
                        const { value, done } = await reader.read();
                        if (done) break;

                        buffer += decoder.decode(value, { stream: true });

                        // Google sends a JSON array of objects, but in stream it might come as separate JSON objects or a list
                        // The format is typically: [{...}, \n {...}] or just {...} \n {...}
                        // We need to parse complete JSON objects.

                        // Simple parsing strategy: split by newlines and try to parse
                        // Note: This is a simplification. A more robust parser handles partial JSONs.
                        // However, Google's stream usually sends complete JSON objects per chunk or line.

                        // Actually, Google's stream format is a JSON array where each item is a chunk.
                        // But in raw stream, it often comes as `,\r\n` separated objects inside `[` and `]`.

                        // Let's try a regex approach to find complete JSON objects or just accumulate text
                        // A common pattern for Google stream is that it starts with '[' and ends with ']'
                        // and chunks are separated by commas.

                        // Clean up buffer to make it easier to parse
                        let cleanBuffer = buffer;
                        if (cleanBuffer.startsWith('[')) cleanBuffer = cleanBuffer.substring(1);
                        if (cleanBuffer.endsWith(']')) cleanBuffer = cleanBuffer.substring(0, cleanBuffer.length - 1);

                        // Split by comma might be dangerous if content has commas.
                        // Better to rely on the structure of the response.
                        // Each chunk is: { "candidates": [ ... ] }

                        // Let's try to match balanced braces for objects
                        let braceCount = 0;
                        let startIndex = 0;

                        for (let i = 0; i < buffer.length; i++) {
                            if (buffer[i] === '{') braceCount++;
                            if (buffer[i] === '}') braceCount--;

                            if (braceCount === 0 && i > startIndex) {
                                // Potential object found
                                const chunkStr = buffer.substring(startIndex, i + 1).trim();
                                if (chunkStr.startsWith('{') && chunkStr.endsWith('}')) {
                                    try {
                                        const parsed = JSON.parse(chunkStr);
                                        const text = parsed.candidates?.[0]?.content?.parts?.[0]?.text || '';
                                        if (text) handleChunk(text);
                                    } catch (e) {
                                        // Ignore parse errors for partial chunks
                                    }
                                }
                                // Move past this object and any following comma/whitespace
                                startIndex = i + 1;
                                while (startIndex < buffer.length && (buffer[startIndex] === ',' || buffer[startIndex] === '\n' || buffer[startIndex] === '\r' || buffer[startIndex] === ' ')) {
                                    startIndex++;
                                }
                            }
                        }

                        // Keep the unparsed part in buffer
                        // Special case: if we are at the very beginning and see '[', skip it
                        if (startIndex === 0 && buffer.trim().startsWith('[')) {
                            startIndex = buffer.indexOf('[') + 1;
                        }

                        buffer = buffer.substring(startIndex);
                    }
                    handleDone();
                } catch (err) {
                    handleError(err);
                }

            } else {
                setLoading(false);
                if (!abortController.signal.aborted) {
                    updateLastMessage("No API key or provider selected.");
                } else {
                    deleteEmptyMessage();
                }
                abortControllerRef.current = null;
            }
        } catch (e: any) {
            setLoading(false);
            deleteEmptyMessage();
            // Show error in the UI instead of just console
            if (!(e && (e.name === 'AbortError' || e.message === 'BodyStreamBuffer was aborted'))) {
                const errorMsg = e?.message || 'An error occurred while communicating with the AI provider.';
                showError('Co-pilot Error', errorMsg);
            }
            abortControllerRef.current = null;
        }
        if (!finished) {
            setLoading(false);
            abortControllerRef.current = null;
        }
    };

    // Append message, optionally with cost
    const appendMessage = (
        sender: string,
        message: string,
        extra?: { cost?: number; prompt_tokens?: number; completion_tokens?: number }
    ) => {
        setChatMessages((prevMessages) => [...prevMessages, { sender, message, ...(extra || {}) }]);
    };

    // Update the last message in chatMessages (for streaming)
    const updateLastMessage = (message: string) => {
        setChatMessages((prevMessages) => {
            if (prevMessages.length === 0) return prevMessages;
            const updated = [...prevMessages];
            updated[updated.length - 1] = { ...updated[updated.length - 1], message };
            return updated;
        });
    };

    // (updateLastMessageCost removed - unused)

    const deleteEmptyMessage = () => {
        setChatMessages((prevMessages) => prevMessages.slice(0, -1));
    };

    // (processStream removed - unused)


    const clearHistory = () => {
        setChatMessages([])
        setQuestion('')
        setTotalCost(0)
    }

    // Cancel handler for aborting the AI request
    const handleCancel = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setLoading(false);
        deleteEmptyMessage();
    }

    const copyToClipboard = () => {
        if (navigator.clipboard) {
            // Modern Clipboard API (Requires HTTPS)
            navigator.clipboard.writeText(question)
                .then(() => {
                    setTooltipText('Copied!');
                    setTimeout(() => setTooltipText(''), 1000);
                })
                .catch((err) => console.error('Clipboard write failed:', err));
        } else {
            // Fallback for HTTP: Use `execCommand`
            const textArea = document.createElement('textarea');
            textArea.value = question;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setTooltipText('Copied!');
                setTimeout(() => setTooltipText(''), 1000);
            } catch (err) {
                console.error('Fallback copy failed:', err);
            }
            document.body.removeChild(textArea);
        }
    };


    const handleTextareaChange = (e: any) => {
        setQuestion(e.target.value);  // Update the 'question' state with the new value
        setUserEditedPrompt(true);
    };
    const clearSettings = () => {
        window.localStorage.removeItem('openAIProvider')
        window.localStorage.removeItem('openAIModel')
        window.localStorage.removeItem('openRouterKEY')
        window.localStorage.removeItem('googleKEY')
        window.localStorage.removeItem('OpenAI')
        window.localStorage.removeItem('openAIKEY')
        window.localStorage.removeItem('openAITerms')
        window.localStorage.removeItem('useSystemSettings')
        window.localStorage.removeItem('azureOpenAIEndpoint')
        window.localStorage.removeItem('azureOpenAIKEY')
        // Reset state variables
        setLocalKeyInput('')
        setLocalOpenRouterKeyInput('')
        setLocalGoogleKeyInput('')
        setLocalProvider('OpenAI')
        setLocalModel('gpt-4-turbo')
        setAiProvider('OpenAI')
        setOpenAiModel('gpt-4-turbo')
        setAgreeTerms(false)
        setSavedKey('')
        setOpenRouterKey('')
        setAzureEndpoint('')
        setLocalAzureEndpoint('')
        setGoogleKey('')
        setAzureKey('')
        // Reset system settings checkbox
        setUseSystemSettings(false)
    }

    const handleSaveSettings = async () => {
        // Save the useSystemSettings state to localStorage
        window.localStorage.setItem('useSystemSettings', JSON.stringify(useSystemSettings));

        if (useSystemSettings) {
            // When using system settings, no validation needed as values are from system
            setAiProvider(localProvider);
            setOpenAiModel(localModel);
            if (localProvider === 'OpenAI' || localProvider === 'AzureOpenAI') {
                setSavedKey(localKeyInput);
            } else if (localProvider === 'OpenRouter') {
                setOpenRouterKey(localOpenRouterKeyInput);
            } else if (localProvider === 'Google') {
                setGoogleKey(localGoogleKeyInput);
            }
            // Save provider and model to localStorage
            window.localStorage.setItem('openAIProvider', localProvider);
            window.localStorage.setItem('openAIModel', localModel);
            if (localProvider === 'AzureOpenAI') {
                // For Azure with system settings, ensure Target URI is populated
                setAzureEndpoint(localAzureEndpoint || azureEndpoint);
            }
            setShowSettings(false);
            return;
        }

        let keyToValidate = '';
        if (localProvider === 'OpenAI') {
            keyToValidate = localKeyInput;
            // Validate OpenAI Base URL if provided
            if (localOpenAiBaseUrl && localOpenAiBaseUrl.trim() !== '') {
                const trimmedUrl = localOpenAiBaseUrl.trim();
                if (!trimmedUrl.startsWith('https://')) {
                    showError('Co-pilot Error', 'OpenAI Base URL must start with https://');
                    return;
                }
                try {
                    new URL(trimmedUrl);
                } catch {
                    showError('Co-pilot Error', 'Please enter a valid OpenAI Base URL');
                    return;
                }
            }
        } else if (localProvider === 'OpenRouter') {
            keyToValidate = localOpenRouterKeyInput;
        } else if (localProvider === 'AzureOpenAI') {
            keyToValidate = localKeyInput;
        } else if (localProvider === 'Google') {
            keyToValidate = localGoogleKeyInput;
        }

        const errorMsg = await validateApiKey(localProvider, keyToValidate);
        if (errorMsg) {
            showError('Co-pilot Error', errorMsg);
            return;
        }

        // Save and close modal if valid
        if (localProvider === 'OpenAI') {
            setSavedKey(localKeyInput);
            window.localStorage.setItem('openAIKEY', btoa(localKeyInput));
            window.localStorage.setItem('openAIBaseUrl', localOpenAiBaseUrl);
            setOpenAiBaseUrl(localOpenAiBaseUrl);
        } else if (localProvider === 'AzureOpenAI') {
            setSavedKey(localKeyInput);
            window.localStorage.setItem('azureOpenAIKEY', btoa(localKeyInput));
            window.localStorage.setItem('azureOpenAIEndpoint', localAzureEndpoint);
            setAzureEndpoint(localAzureEndpoint);
            setAzureKey(localKeyInput);
        } else if (localProvider === 'OpenRouter') {
            setOpenRouterKey(localOpenRouterKeyInput);
            window.localStorage.setItem('openRouterKEY', btoa(localOpenRouterKeyInput));
        } else if (localProvider === 'Google') {
            setGoogleKey(localGoogleKeyInput);
            window.localStorage.setItem('googleKEY', btoa(localGoogleKeyInput));
        }
        // Save provider and model to localStorage
        window.localStorage.setItem('openAIProvider', localProvider);
        window.localStorage.setItem('openAIModel', localModel);
        setAiProvider(localProvider);
        setOpenAiModel(localModel);
        setShowSettings(false);
    };

    const terms = <>
        <p>Before you proceed, we would like you to read, and agree to the following:</p>
        <ol>
            <li><strong>Company Policy. </strong>Any use you may make of our AI integration is in
                accordance with your organisation’s policies.
            </li>
            <li><strong>Testing. </strong>In accordance with best practice, we strongly recommend that you
                evaluate any recommendations, and test to your own satisfaction, before deploying in a
                production environment.
            </li>
            <li><strong>Consequence. </strong>DBmarlin Co-pilot, through its use of AI, may make
                suggestions that have further consequences, for example, on your database licensing. If you
                choose to follow those recommendations, then it is your own responsibility to evaluate the
                consequential impact of such recommendations.
            </li>
            <li><strong>No Liability. </strong>AI vendors cannot guarantee the accuracy of their
                recommendations. DBmarlin cannot take any responsibility or liability for any changes that
                you choose to make as a result.
            </li>
        </ol>
    </>

    return (
        <>
            {/* If provider is None, always show the prompt-to-copy UI */}
            <ConditionalRender if={aiProvider === "None"}>
                <div className="api-key-container">
                    {/* <div className='ai-logo'/> */}
                    <div className='ai-heading'><i className="fal fa-microchip-ai me-1" aria-hidden="true"></i>DBmarlin Co-pilot</div>
                    <div className="card">
                        <div className="card-header">
                            Use your own LLM
                        </div>
                        <div className='p-2'>
                            <textarea value={question}
                                onChange={handleTextareaChange}
                                className="form-control mb-2" />
                            <p className='text-end'>
                                <button
                                    type="button"
                                    className="btn btn-copy"
                                    onClick={copyToClipboard}
                                >
                                    <i className="fal fa-copy fa-fw"></i> {tooltipText === 'Copied!' ? tooltipText : 'Copy prompt'}
                                </button>
                            </p>
                        </div>
                    </div>
                    <p className='swap-ai'>To use an API provider, <span onClick={() => setShowSettings(true)}>open AI Settings</span>.</p>
                </div>
            </ConditionalRender>
            {/* AI Settings Modal */}
            {showSettings && (
                <div className="ai-settings-modal">
                    <div className="modal-content">
                        <h2 className="modal-title">AI Settings</h2>


                        {/* System Settings Checkbox */}
                        {systemSettingsAvailable && localProvider !== "None" && (
                            <div className="mb-3">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        id="useSystemSettings"
                                        className="form-check-input"
                                        checked={useSystemSettings}
                                        onChange={(e) => handleSystemSettingsChange(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="useSystemSettings">
                                        Apply system settings (configured by administrator)
                                    </label>
                                </div>
                                <small className="form-text text-muted">
                                    When checked, fields will be populated and disabled using <a href="/admin/integrations/co-pilot/">system-configured values</a>.
                                </small>
                            </div>
                        )}


                        <div className="mb-3">
                            <label htmlFor="ai-provider-select" className="form-label">
                                Provider:{useSystemSettings ? ` (${localProvider})` : ''}
                            </label>
                            <div className="select-wrapper">
                                <select
                                    id="ai-provider-select"
                                    className="form-control"
                                    value={localProvider}
                                    onChange={e => saveProvider(e.target.value)}
                                    disabled={useSystemSettings}
                                >
                                    <option value="OpenAI">OpenAI</option>
                                    <option value="OpenRouter">OpenRouter</option>
                                    <option value="Google">Google</option>
                                    <option value="AzureOpenAI">Microsoft Azure OpenAI</option>
                                    <option value="None">None</option>
                                </select>
                                {/* Dropdown arrow icon */}
                                <span className="dropdown-arrow">
                                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 8L10 12L14 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            </div>
                        </div>


                        {localProvider !== "None" && (
                            <>
                                {localProvider === "OpenAI" && (
                                    <div className="mb-3">
                                        <label htmlFor="openai-base-url" className="form-label">
                                            Base URL:
                                        </label>
                                        <input
                                            id="openai-base-url"
                                            type="text"
                                            className="form-control"
                                            placeholder="https://api.openai.com"
                                            value={useSystemSettings ? openAiBaseUrl : localOpenAiBaseUrl}
                                            onChange={e => setLocalOpenAiBaseUrl(e.target.value)}
                                            disabled={useSystemSettings}
                                            style={{ backgroundColor: useSystemSettings ? '#f8f9fa' : 'white' }}
                                        />
                                        <small className="form-text text-muted">
                                            Optional. Defaults to https://api.openai.com. Use https://us.api.openai.com for US customers.
                                        </small>
                                    </div>
                                )}

                                {localProvider === "AzureOpenAI" && (
                                    <>
                                        <div className="mb-3">
                                            <label htmlFor="azure-endpoint" className="form-label">
                                                Azure Target URI:
                                            </label>
                                            <input
                                                id="azure-endpoint"
                                                type="text"
                                                className="form-control"
                                                placeholder="Full Azure chat URI copied from the portal"
                                                value={useSystemSettings ? azureEndpoint : localAzureEndpoint}
                                                onChange={e => setLocalAzureEndpoint(e.target.value)}
                                                disabled={useSystemSettings}
                                                style={{ backgroundColor: useSystemSettings ? '#f8f9fa' : 'white' }}
                                            />
                                        </div>
                                        <div className="mb-3 text-muted">
                                            Model will be derived from the Azure Target URI you configure.
                                        </div>
                                    </>
                                )}

                                {(localProvider === "OpenAI" || localProvider === "OpenRouter" || localProvider === "Google") && (
                                    <div className="mb-3">
                                        <label htmlFor="ai-model-select" className="form-label">
                                            {localProvider === "OpenAI"
                                                ? "OpenAI Model:" : localProvider === "Google" ? "Google Model:"
                                                    : "OpenRouter Model:"}{useSystemSettings ? ` (${localModel})` : ''}
                                        </label>
                                        <div className="model-select-table">
                                            <table className="table table-hover mb-0">
                                                <thead>
                                                    <tr>
                                                        <th
                                                            onClick={() => {
                                                                if (sortColumn === 'model') {
                                                                    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                                                                } else {
                                                                    setSortColumn('model');
                                                                    setSortDirection('asc');
                                                                }
                                                            }}
                                                        >
                                                            Model {sortColumn === 'model' && (sortDirection === 'asc' ? '▲' : '▼')}
                                                        </th>
                                                        <th
                                                            onClick={() => {
                                                                if (sortColumn === 'input') {
                                                                    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                                                                } else {
                                                                    setSortColumn('input');
                                                                    setSortDirection('asc');
                                                                }
                                                            }}
                                                        >
                                                            Input Cost {sortColumn === 'input' && (sortDirection === 'asc' ? '▲' : '▼')}
                                                        </th>
                                                        <th
                                                            onClick={() => {
                                                                if (sortColumn === 'output') {
                                                                    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                                                                } else {
                                                                    setSortColumn('output');
                                                                    setSortDirection('asc');
                                                                }
                                                            }}
                                                        >
                                                            Output Cost {sortColumn === 'output' && (sortDirection === 'asc' ? '▲' : '▼')}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(localProvider === "OpenAI" ? openAiModels : localProvider === "Google" ? googleModels : openRouterModels)
                                                        .map(m => {
                                                            // Get model pricing and costs as before
                                                            const modelName = m.value;

                                                            const providerForPricing = localProvider === "OpenRouter" ? "OpenRouter" : "OpenAI";
                                                            const p = resolveModelPricing(pricing, FALLBACK_PRICING, providerForPricing, modelName) as ModelPricing | null;

                                                            const inputCost = p?.input_cost_per_1k_tokens ?? (p?.input_cost_per_token != null ? p.input_cost_per_token * 1000 : null);
                                                            const outputCost = p?.output_cost_per_1k_tokens ?? (p?.output_cost_per_token != null ? p.output_cost_per_token * 1000 : null);

                                                            return {
                                                                model: m,
                                                                inputCost,
                                                                outputCost,
                                                                label: m.label
                                                            };
                                                        })
                                                        // Sort the array based on current sort column and direction
                                                        .sort((a, b) => {
                                                            let comparison = 0;
                                                            if (sortColumn === 'model') {
                                                                comparison = a.label.localeCompare(b.label);
                                                            } else if (sortColumn === 'input') {
                                                                // Handle null values and sort numerically
                                                                if (a.inputCost === null && b.inputCost === null) comparison = 0;
                                                                else if (a.inputCost === null) comparison = 1;
                                                                else if (b.inputCost === null) comparison = -1;
                                                                else comparison = a.inputCost - b.inputCost;
                                                            } else if (sortColumn === 'output') {
                                                                // Handle null values and sort numerically
                                                                if (a.outputCost === null && b.outputCost === null) comparison = 0;
                                                                else if (a.outputCost === null) comparison = 1;
                                                                else if (b.outputCost === null) comparison = -1;
                                                                else comparison = a.outputCost - b.outputCost;
                                                            }
                                                            return sortDirection === 'asc' ? comparison : -comparison;
                                                        })
                                                        .map(({ model: m, inputCost, outputCost }) => {
                                                            // Format costs
                                                            const formatCost = (cost: number | null) => {
                                                                if (cost === null) return "N/A";
                                                                if (cost === 0) return "Free";
                                                                // Always use fixed decimals to avoid scientific notation.
                                                                // Use more decimals for very small values.
                                                                const decimals = cost < 0.001 ? 7 : 4;
                                                                const fixed = cost.toFixed(decimals);
                                                                // Trim trailing zeros but keep at least 4 decimals
                                                                let [intPart, fracPart = ""] = fixed.split(".");
                                                                fracPart = fracPart.replace(/0+$/, "");
                                                                if (fracPart.length < 4) {
                                                                    fracPart = fracPart.padEnd(4, "0");
                                                                }
                                                                const display = `${intPart}.${fracPart}`;
                                                                return `$${display}/1K`;
                                                            };
                                                            const displayLabel = HIGH_LATENCY_MODELS.has(m.value)
                                                                ? `${m.label} (High latency)`
                                                                : m.label;

                                                            return (
                                                                <tr
                                                                    key={m.value}
                                                                    onClick={() => {
                                                                        if (!useSystemSettings) {
                                                                            setLocalModel(m.value)
                                                                        }
                                                                    }}
                                                                    className={m.value === localModel ? 'selected' : ''}
                                                                    style={{
                                                                        cursor: useSystemSettings ? 'not-allowed' : 'pointer',
                                                                        opacity: useSystemSettings ? 0.6 : 1
                                                                    }}
                                                                >
                                                                        <td>{displayLabel}</td>
                                                                        <td>{formatCost(inputCost)}</td>
                                                                        <td>{formatCost(outputCost)}</td>
                                                                    </tr>
                                                            );
                                                        })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label className="form-label">
                                        {localProvider === "OpenAI"
                                            ? "OpenAI API Key:"
                                            : localProvider === "AzureOpenAI"
                                                ? "Azure OpenAI API Key:"
                                                : localProvider === "Google"
                                                    ? "Google API Key:"
                                                    : "OpenRouter API Key:"}
                                    </label>
                                    {(localProvider === "OpenAI" || localProvider === "AzureOpenAI") ? (
                                        <input
                                            type="password"
                                            className="form-control mt-2"
                                            placeholder={useSystemSettings
                                                ? "System-configured API Key"
                                                : (localProvider === "AzureOpenAI" ? "Enter Azure OpenAI API Key" : "Enter OpenAI API Key")}
                                            value={localKeyInput}
                                            onChange={e => setLocalKeyInput(e.target.value)}
                                            disabled={useSystemSettings}
                                            style={{ backgroundColor: useSystemSettings ? '#f8f9fa' : 'white' }}
                                        />
                                    ) : localProvider === "Google" ? (
                                        <input
                                            type="password"
                                            className="form-control mt-2"
                                            placeholder={useSystemSettings ? "System-configured API Key" : "Enter Google API Key"}
                                            value={localGoogleKeyInput}
                                            onChange={e => setLocalGoogleKeyInput(e.target.value)}
                                            disabled={useSystemSettings}
                                            style={{ backgroundColor: useSystemSettings ? '#f8f9fa' : 'white' }}
                                        />
                                    ) : (
                                        <input
                                            type="password"
                                            className="form-control mt-2"
                                            placeholder={useSystemSettings ? "System-configured API Key" : "Enter OpenRouter API Key"}
                                            value={localOpenRouterKeyInput}
                                            onChange={e => setLocalOpenRouterKeyInput(e.target.value)}
                                            disabled={useSystemSettings}
                                            style={{ backgroundColor: useSystemSettings ? '#f8f9fa' : 'white' }}
                                        />
                                    )}
                                    <small className="form-text text-muted">
                                        {useSystemSettings
                                            ? "This API key is configured by your system administrator."
                                            : "Your API key is securely stored in your browser."}
                                    </small>
                                </div>
                            </>
                        )}
                        <div className="modal-footer">

                            <button type="button" className="clear-settings" onClick={() => clearSettings()}>Clear Settings</button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowSettings(false)}
                                type="button"
                            >
                                <i className="fas fa-times me-2"></i>
                                Close
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSaveSettings}
                                type="button"
                            >
                                <i className="fas fa-save me-2"></i>
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ConditionalRender if={!hasValidProviderKey() && aiProvider !== "None"}>
                <ConditionalRender if={showDBmarlin}>
                    <div className="api-key-container">
                        {/* <div className='ai-logo'/> */}
                        <div className='ai-heading mb-5 mt-4'><i className="fal fa-microchip-ai me-1" aria-hidden="true"></i>DBmarlin Co-pilot</div>
                        <p><strong>DBmarlin Co-pilot</strong> makes use of <strong>OpenAI</strong>, <strong>OpenRouter</strong> or <strong>Microsoft Azure OpenAI</strong> which requires you to
                            enter your unique API key from the AI vendor.
                        </p>
                        <ol>
                            <li><strong>Local Storage Only: </strong>Your API key will only be stored locally within your
                                browser, in an encrypted format. We do not save or access your key on any external servers.
                            </li>
                            <li><strong>Never Share Your Key: </strong>As is usual, do not share your key with anyone, and
                                be cautious of phishing attempts or unauthorised requests.
                            </li>
                            <li><strong>Secure Input Process: </strong>Please enter your API key in the secure input field
                                provided.
                            </li>
                            <li><strong>Security Best Practices: </strong>Regularly monitor and review the usage of your API
                                key to detect any unusual activity.
                            </li>
                        </ol>

                        <h3 className="text-center my-4 h3-title">Considerations for using AI</h3>

                        <div className="terms-container">
                            {!agreeTerms && terms}
                        </div>

                        <div className="col-12">
                            <div className="form-check form-switch text-center">
                                <input
                                    type="checkbox"
                                    id="terms"
                                    onChange={() => {
                                        setAgreeTerms(!agreeTerms)
                                        window.localStorage.setItem('openAITerms', 'true')
                                    }}
                                    className='pointer'
                                    checked={agreeTerms}
                                    data-lpignore={true}
                                />
                                <label htmlFor='terms' className="reminder pointer user-select-none">&nbsp;I accept these
                                    conditions</label>
                            </div>
                        </div>
                        <div className="text-center my-4 py-2">
                            <button
                                className="btn btn-primary px-4 py-2"
                                onClick={() => setShowSettings(true)}
                                disabled={!agreeTerms}
                            >
                                <i className="fas fa-cog me-2"></i>
                                AI Settings
                            </button>
                        </div>

                    </div>

                    <p className='swap-ai'>Click here to <span onClick={() => setShowDBmarlin(!showDBmarlin)}>view prompt</span> and use your own LLM.</p>
                </ConditionalRender>

                <ConditionalRender if={!showDBmarlin}>
                    <div className="api-key-container">
                        {/* <div className='ai-logo'/> */}
                        <div className='ai-heading'><i className="fal fa-microchip-ai me-1" aria-hidden="true"></i>DBmarlin Co-pilot</div>

                        <div className="card">
                            <div className="card-header">
                                Use your own LLM
                            </div>
                            <div className='p-2'>
                                <textarea value={question}
                                    onChange={handleTextareaChange}
                                    className="form-control mb-2" />
                                <p className='text-end'>
                                    <button
                                        type="button"
                                        className="btn btn-copy"
                                        onClick={copyToClipboard}
                                    >
                                        <i className="fal fa-copy fa-fw"></i> {tooltipText === 'Copied!' ? tooltipText : 'Copy prompt'}
                                    </button>
                                </p>
                            </div>
                        </div>
                    </div>
                    <p className='swap-ai'>Click here to <span onClick={() => setShowDBmarlin(!showDBmarlin)}>configure DBmarlin Co-pilot</span> to use an API key.</p>
                </ConditionalRender>
            </ConditionalRender>
            <ConditionalRender if={hasValidProviderKey()}>
                <div className="chat-container">
                    <div className="sticky-header">
                        <div className="header-content">
                            <div className="header-title">
                                <i className="fal fa-microchip-ai me-1" aria-hidden="true"></i>
                                DBmarlin Co-pilot
                            </div>
                            <span className="cost-badge total-cost">
                                ${totalCost.toFixed(3)}
                            </span>
                        </div>
                    </div>
                    <div className="chat-content">
                        <ConditionalRender if={!chatMessages.length && !agreeTerms}>
                            {terms}
                        </ConditionalRender>
                        <div className="message-list" ref={chatContainerRef}>
                            {chatMessages.map((msg, index) => (
                                <div key={index} className={`message ${msg.sender === DBMARLIN_AI ? 'gpt' : ''}`}>
                                    <div className='message-box'>
                                        <div className="message-header">
                                            <strong className="sender-name">
                                                {msg.sender}:
                                                {/* Show estimated tokens and cost if available */}
                                                {msg.sender === DBMARLIN_AI && typeof msg.prompt_tokens === "number" && typeof msg.completion_tokens === "number" && (
                                                    <span className="cost-badge">
                                                        {typeof msg.cost === "number"
                                                            ? <>${msg.cost.toFixed(7)}&nbsp;</>
                                                            : null}
                                                        &bull; in: {msg.prompt_tokens} &bull; out: {msg.completion_tokens}
                                                    </span>
                                                )}
                                            </strong>
                                            {msg.sender === DBMARLIN_AI && typeof msg.completion_tokens === "number" && (
                                                <button
                                                    className={`message-copy-button ${copiedMessage === index ? 'copied' : ''}`}
                                                    onClick={() => copyToClipboardWithFeedback(msg.message, setCopiedMessage, index)}
                                                >
                                                    <i className="fas fa-copy"></i>
                                                    {copiedMessage === index ? 'Copied!' : 'Copy'}
                                                </button>
                                            )}
                                        </div>
                                        {msg.sender === DBMARLIN_AI ? (
                                            <ReactMarkdown
                                                className="message-content markdown-content"
                                                remarkPlugins={[remarkGfm]}
                                                skipHtml={true}
                                                components={{
                                                    table: ({ children, ...props }: { children: React.ReactNode } & React.HTMLAttributes<HTMLTableElement>) => (
                                                        <table className="table table-bordered" {...props}>{children}</table>
                                                    ),
                                                    thead: ({ children, ...props }: { children: React.ReactNode } & React.HTMLAttributes<HTMLTableSectionElement>) => (
                                                        <thead className="table-light" {...props}>{children}</thead>
                                                    ),
                                                    th: ({ children, ...props }: { children: React.ReactNode } & React.ThHTMLAttributes<HTMLTableHeaderCellElement>) => (
                                                        <th scope="col" {...props}>{children}</th>
                                                    ),
                                                    code: ({ inline, className, children, ...props }: { inline?: boolean, className?: string, children: React.ReactNode }) => {
                                                        const match = /language-(\w+)/.exec(className || '');
                                                        const codeString = String(children).replace(/\n$/, '');

                                                        return !inline && match ? (
                                                            <div className="code-container">
                                                                <SyntaxHighlighter
                                                                    style={oneDark as any}
                                                                    language={match[1]}
                                                                    PreTag="div"
                                                                    {...props}
                                                                >
                                                                    {codeString}
                                                                </SyntaxHighlighter>
                                                                <button
                                                                    className={`code-copy-button ${copiedCode === codeString ? 'copied' : ''}`}
                                                                    onClick={() => copyToClipboardWithFeedback(codeString, setCopiedCode, codeString)}
                                                                >
                                                                    <i className={`fas ${copiedCode === codeString ? 'fa-check' : 'fa-copy'}`}></i>
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <code className={className} {...props}>
                                                                {children}
                                                            </code>
                                                        );
                                                    }
                                                }}
                                            >
                                                {msg.message}
                                            </ReactMarkdown>
                                        ) : (
                                            <div className="message-content">{msg.message}</div>
                                        )}
                                    </div>
                                    <ConditionalRender if={loading && index === chatMessages.length - 1}>
                                        <div className="chat-loader"></div>
                                    </ConditionalRender>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="open-ai-input-wrapper position-relative">
                    <input
                        type="text"
                        placeholder={`Message ${DBMARLIN_AI}`}
                        readOnly={loading}
                        className="form-control open-ai-form-control"
                        onChange={(e) => {
                            setQuestion(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                askGPT();
                            }
                        }}
                        value={question}
                    />
                    <button
                        className="open-ai-input-button btn btn-sm btn-primary"
                        disabled={!question || loading || !agreeTerms}
                        onClick={() => askGPT('')}
                        type="button"
                    >
                        {!loading ? <i className="fas fa-arrow-up"></i> : 'Loading...'}
                    </button>
                    {loading && (
                        <button
                            className="btn btn-sm btn-secondary cancel-button"
                            onClick={handleCancel}
                            type="button"
                            tabIndex={0}
                        >
                            Cancel
                        </button>
                    )}
                </div>
                <div
                    className='d-flex justify-content-between'>
                    <p className='open-ai-info'>
                        <button
                            type="button"
                            className='open-ai-link-button'
                            onClick={() => clearHistory()}
                        >
                            Clear History
                        </button>
                    </p>
                    <p className='open-ai-info'>AI assistants can make mistakes. Consider checking important information. ({openAiModel})</p>
                    <p className='open-ai-info'>
                        <button
                            type="button"
                            className='open-ai-link-button'
                            onClick={() => setShowSettings(true)}
                        >
                            AI Settings
                        </button>
                    </p>
                </div>

            </ConditionalRender>
        </>
    )
        ;
};

export default ChatComponent;
